import React, { useState, useEffect } from 'react';
import { useDefaultItemOwner } from './useDefaultItemOwner';
import { useEscapeToClose } from '../common/useEscapeToClose';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { createSafeCloseHandler } from '../../utils/modalUtils';
import { RISK_OPTIONS, RiskLevel } from '../../constants/project';

interface AddActionItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (itemData: any) => Promise<void>;
  projectData: ProjectInterface;
  users: any[];
}

interface ActionItemFormData {
  name: string;
  description: string;
  owner_id: string;
  assigned_to: string;
  due_date: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  risks: RiskLevel;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD';
}

export const AddActionItemModal: React.FC<AddActionItemModalProps> = ({
  isOpen,
  onClose,
  onAddItem,
  projectData,
  users: usersFromProject,
}) => {
  const { keycloak: { idTokenParsed } } = useKeycloak();
  
  // Find current user in project users by matching email
  const users = usersFromProject;
  // The logged-in user's Keycloak subject is their user id, the same id the project user list uses.
  const defaultOwnerId = useDefaultItemOwner(users, projectData?.owner_id, idTokenParsed?.sub);
  
  const [formData, setFormData] = useState<ActionItemFormData>({
    name: '',
    description: '',
    owner_id: defaultOwnerId, // Default owner, changeable
    assigned_to: '',
    due_date: new Date().toISOString().split('T')[0],
    priority: 'LOW',
    risks: RISK_OPTIONS[0].value,
    status: 'NOT_STARTED'
  });
  const [isLoading, setIsLoading] = useState(false);

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['owner_id', 'assigned_to', 'due_date', 'priority', 'status'] // Exclude auto-filled fields
  );

  useEscapeToClose(isOpen, onClose);

  // Set default owner_id only when modal first opens
  useEffect(() => {
    if (isOpen && defaultOwnerId) {
      setFormData(prev => ({
        ...prev,
        owner_id: defaultOwnerId
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only run when modal opens/closes

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Action item name is required');
      return;
    }
    if (!formData.owner_id || !formData.owner_id.trim()) {
      toast.error('Owner is required');
      return;
    }
    if (!formData.status) {
      toast.error('Status is required');
      return;
    }

    setIsLoading(true);
    try {
      const baseData = {
        name: formData.name,
        description: formData.description,
        priority: formData.priority,
        risks: formData.risks || null,
        status: formData.status,
        project_id: projectData.id,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : undefined,
      };
      
      // Only add owner_id and assigned_to if they have valid UUID values
      const itemData: any = { ...baseData };
      if (formData.owner_id && formData.owner_id.trim()) {
        itemData.owner_id = formData.owner_id;
      }
      if (formData.assigned_to && formData.assigned_to.trim()) {
        itemData.assigned_to = formData.assigned_to;
      }
      
      console.log('AddActionItemModal: submitting item data', itemData);
      
      await onAddItem(itemData);
      
      // The success message is shown by the list handler that performs the create.
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        owner_id: defaultOwnerId, // Keep the default owner
        assigned_to: '',
        due_date: new Date().toISOString().split('T')[0],
        priority: 'LOW',
        status: 'NOT_STARTED',
        risks: RISK_OPTIONS[0].value
      });
      
      onClose();
    } catch (error) {
      console.error('AddActionItemModal: failed to add action item', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add action item';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center"
      style={{ zIndex: 1000000 }}
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 text-[#aaa] hover:text-[#555] p-2"
        style={{ zIndex: 1000001 }}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Add New Action Item</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#444] mb-1">
                Action Item Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                maxLength={255}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              />
              {formData.name.length > 200 && (
                <p className={`mt-1 text-xs ${formData.name.length >= 255 ? 'text-red-500' : 'text-[#aaa]'}`}>
                  {formData.name.length}/255
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#444] mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Owner <span className="text-red-500">*</span>
              </label>
              <select
                name="owner_id"
                value={formData.owner_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              >
                <option value="">Select owner</option>
                {(users || []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Assignee
              </label>
              <select
                name="assigned_to"
                value={formData.assigned_to}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                <option value="">Select assignee</option>
                {(users || []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Risk Level
              </label>
              <select
                name="risks"
                value={formData.risks}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                {RISK_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              >
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#444] bg-[#f5f5f5] rounded-md hover:bg-[#eaeaea] focus:outline-none focus:ring-0"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#171717] focus:outline-none focus:ring-0 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add Action Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 